import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, unwrap } from "../client";
import { noErrorToast } from "../mutationMeta";
import type { Session } from "../types";

export const sessionKey = ["session"] as const;

export function useSession() {
  return useQuery({
    queryKey: sessionKey,
    queryFn: () => unwrap(api.GET("/api/auth/session/")),
    staleTime: 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast, // LoginPage and InvitePage render it
    mutationFn: (body: { email: string; password: string }) => unwrap(api.POST("/api/auth/login/", { body })),
    onSuccess: (session: Session) => {
      qc.clear();
      qc.setQueryData(sessionKey, session);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast, // signs out locally either way
    // Optional same-app destination after signing out (e.g. back to an invitation).
    mutationFn: (_redirectTo?: string) => unwrap(api.POST("/api/auth/logout/")),
    onSettled: (_data, _error, redirectTo) => {
      qc.clear();
      window.location.assign(redirectTo?.startsWith("/") && !redirectTo.startsWith("//") ? redirectTo : "/login");
    },
  });
}

export function useLoginHint() {
  return useQuery({
    queryKey: ["login-hint"],
    queryFn: () => unwrap(api.GET("/api/auth/login-hint/")),
    staleTime: Infinity,
  });
}

export function useForgetLoginHint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.DELETE("/api/auth/login-hint/")),
    onSuccess: () => qc.setQueryData(["login-hint"], { tenant: null }),
  });
}

export function useSelectTenant() {
  return useMutation({
    meta: noErrorToast, // background bookkeeping (remembers the last workspace)
    mutationFn: (tenant_slug: string) => unwrap(api.POST("/api/auth/select-tenant/", { body: { tenant_slug } })),
  });
}

export function useStopImpersonation() {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast, // reloads the platform area either way
    mutationFn: () => unwrap(api.POST("/api/auth/impersonation/stop/")),
    onSettled: () => {
      qc.clear();
      window.location.assign("/platform");
    },
  });
}

export function useVersion() {
  return useQuery({
    queryKey: ["version"],
    queryFn: () => unwrap(api.GET("/api/meta/version/")),
    staleTime: Infinity,
  });
}

/** The supported time zone registry (the only values a tenant may store). Offsets shift with DST, so refresh hourly. */
export function useTimeZones() {
  return useQuery({
    queryKey: ["timezones"],
    queryFn: () => unwrap(api.GET("/api/meta/timezones/")),
    staleTime: 60 * 60_000,
  });
}

export function useInvitationPreview(token: string | null) {
  return useQuery({
    queryKey: ["invitation-preview", token],
    enabled: Boolean(token),
    queryFn: () => unwrap(api.POST("/api/invitations/preview/", { body: { token: token ?? "" } })),
    retry: false,
  });
}

export function useAcceptInvitation() {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (token: string) => unwrap(api.POST("/api/invitations/accept/", { body: { token } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionKey }),
  });
}

export function useRegisterInvitation() {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (body: { token: string; first_name: string; last_name: string; password: string }) =>
      unwrap(api.POST("/api/invitations/register/", { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionKey }),
  });
}

export function usePasswordResetPreview(token: string | null) {
  return useQuery({
    queryKey: ["password-reset-preview", token],
    enabled: Boolean(token),
    queryFn: () => unwrap(api.POST("/api/password-reset/preview/", { body: { token: token ?? "" } })),
    retry: false,
    staleTime: Infinity,
  });
}

/** Sets the new password and signs in as that account (the response is the new session). */
export function useCompletePasswordReset() {
  const qc = useQueryClient();
  return useMutation({
    meta: noErrorToast,
    mutationFn: (body: { token: string; password: string }) =>
      unwrap(api.POST("/api/password-reset/complete/", { body })),
    onSuccess: (session: Session) => {
      qc.clear();
      qc.setQueryData(sessionKey, session);
    },
  });
}
